import React, { useState, useEffect, useMemo, useCallback } from "react";
import { IoDocuments } from "react-icons/io5";
import {
  MdDashboard,
  MdOutlineCheckCircle,
  MdOutlineCancel,
  MdOutlineGroup,
  MdAdminPanelSettings,
  MdWorkspaces,
  MdLibraryBooks,
  MdVerifiedUser,
  MdOutlineGroups,
  MdRefresh,
  MdOutlineThumbUp,
} from "react-icons/md";
import Widget from "components/widget/Widget";
import dashboardService from "service/dashboard.service";
import useUserRole from "utils/useUserRole";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="mb-4 text-xl font-bold text-gray-800">{title}</h2>
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-6">
      {children}
    </div>
  </section>
);

const Dashboard = React.memo(() => {
  const [stats, setStats] = useState({
    articles: {
      total: 0,
      nouveau: 0,
      partage_comite: 0,
      en_cours_evaluation: 0,
      revision_demande: 0,
      accepte_publication: 0,
      published: 0,
      rejet: 0,
      passe_secretaire: 0,
    },
    users: { total: 0, admins: 0, secreataires: 0, comites: 0, authors: 0 },
    affiliations: { total: 0, valid: 0, invalid: 0 },
    rubriques: { total: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userRole = useUserRole();

  const fetchStats = useCallback(() => {
    setLoading(true);
    setError(null);
    dashboardService
      .getStats()
      .then((data) => setStats(data))
      .catch(() => setError("Impossible de charger les statistiques"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Section Articles
  const articleWidgets = useMemo(
    () => [
      {
        id: "articles_total",
        icon: <IoDocuments className="h-6 w-6 text-yellow-500" />,
        title: "Totale des articles",
        url: "/admin/articles",
        subtitle: stats.articles.total,
      },
      {
        id: "nouveau",
        icon: <MdDashboard className="h-6 w-6 text-indigo-500" />,
        title: "Nouveaux articles",
        url: "/admin/articles/nouveaux",
        subtitle: stats.articles.nouveau,
      },
      /* {
        id: "partage_comite",
        icon: <MdOutlineGroups className="h-6 w-6 text-cyan-500" />,
        title: "Partagés Comité",
        url: "/admin/articles/partage_comite",
        subtitle: stats.articles.partage_comite,
      },*/
      {
        id: "en_cours_evaluation",
        icon: <MdLibraryBooks className="h-6 w-6 text-blue-500" />,
        title: "Article en cours d’évaluation",
        url: "/admin/articles/partages",
        subtitle: stats.articles.partage_comite,
      },
      {
        id: "revision_demande",
        icon: <MdRefresh className="h-6 w-6 text-purple-500" />,
        title: "Article remanier",
        url: "/admin/articles/remanie",
        subtitle: stats.articles.revision_demande,
      },
      {
        id: "accepte_publication",
        icon: <MdOutlineThumbUp className="h-6 w-6 text-green-500" />,
        title: "Article publiable (En attente de publication)",
        url: "/admin/articles/publiables",
        subtitle: stats.articles.accepte_publication,
      },
      {
        id: "published",
        icon: <MdOutlineCheckCircle className="h-6 w-6 text-green-700" />,
        title: "Articles publiés",
        url: "/admin/articles/publies",
        subtitle: stats.articles.published,
      },
      {
        id: "rejet",
        icon: <MdOutlineCancel className="h-6 w-6 text-red-500" />,
        title: "Articles refusés",
        url: "/admin/articles/refuses",
        subtitle: stats.articles.rejet,
        subtitle: stats.articles.rejet,
      },
      /* {
        id: "passe_secretaire",
        icon: <MdWorkspaces className="h-6 w-6 text-orange-400" />,
        title: "Passés secrétaire",
        subtitle: stats.articles.passe_secretaire,
      },*/
    ],
    [stats.articles]
  );

  // Section Utilisateurs
  const userWidgets = useMemo(
    () => [
      {
        id: "users_total",
        icon: <MdOutlineGroup className="h-7 w-7 text-blue-500" />,
        title: "Total des utilisateurs",
        subtitle: stats.users.total,
      },
      {
        id: "users_admins",
        icon: <MdAdminPanelSettings className="h-7 w-7 text-cyan-600" />,
        title: "Admin",
        subtitle: stats.users.admins,
      },
      {
        id: "users_secreataires",
        icon: <MdWorkspaces className="h-7 w-7 text-orange-400" />,
        title: "Secrétaire",
        subtitle: stats.users.secreataires,
      },
      {
        id: "users_comites",
        icon: <MdOutlineGroups className="h-7 w-7 text-purple-700" />,
        title: "Comités (Relecteur)",
        subtitle: stats.users.comites,
      },
      {
        id: "users_authors",
        icon: <MdVerifiedUser className="h-7 w-7 text-lime-500" />,
        title: "Auteur",
        subtitle: stats.users.authors,
      },
    ],
    [stats.users]
  );

  // Section Affiliations
  const affiliationWidgets = useMemo(
    () => [
      {
        id: "affiliation_total",
        icon: <MdLibraryBooks className="h-7 w-7 text-teal-600" />,
        title: "Nombres d’affiliations",
        subtitle: stats.affiliations.total,
      },
      /* {
        id: "affiliation_valid",
        icon: <MdOutlineCheckCircle className="h-7 w-7 text-green-600" />,
        title: "Valides",
        subtitle: stats.affiliations.valid,
      },
      {
        id: "affiliation_invalid",
        icon: <MdOutlineCancel className="h-7 w-7 text-red-600" />,
        title: "Invalides",
        subtitle: stats.affiliations.invalid,
      },*/
    ],
    [stats.affiliations]
  );

  // Section Rubriques
  const rubriqueWidgets = useMemo(
    () => [
      {
        id: "rubrique_total",
        icon: <MdLibraryBooks className="h-7 w-7 text-pink-600" />,
        title: "Nombres de rubriques",
        url: "/admin/rubriques",
        subtitle: stats.rubriques.total,
      },
    ],
    [stats.rubriques]
  );

  if (loading) return <div>Chargement...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-8">
      <Section title="Articles">
        {articleWidgets.map((w) => (
          <Widget
            key={w.id}
            icon={w.icon}
            title={w.title}
            url={w.url}
            subtitle={w.subtitle}
          />
        ))}
      </Section>
      {userRole === "Administrateur" && (
        <>
          <Section title="Utilisateurs">
            {userWidgets.map((w) => (
              <Widget
                key={w.id}
                icon={w.icon}
                title={w.title}
                subtitle={w.subtitle}
              />
            ))}
          </Section>
          <Section title="Affiliations">
            {affiliationWidgets.map((w) => (
              <Widget
                key={w.id}
                icon={w.icon}
                title={w.title}
                subtitle={w.subtitle}
              />
            ))}
          </Section>
        </>
      )}
      <Section title="Rubriques">
        {rubriqueWidgets.map((w) => (
          <Widget
            key={w.id}
            icon={w.icon}
            title={w.title}
            url={w.url}
            subtitle={w.subtitle}
          />
        ))}
      </Section>
    </div>
  );
});

Dashboard.displayName = "Dashboard";
export default Dashboard;
